import { ParsedUrlQuery } from 'querystring'

import {
  CompactGhostPost,
  GhostPost,
  FrontendUser,
  FrontendUserDetailed,
  Preview,
  BlogPostConfig,
  TInfluencer,
  PreOnboardingTutorialStep,
} from './types'

export const getDistinctIdFromQuery = (query: ParsedUrlQuery) => {
  return query.distinctId as string
}

export const getExperimentsFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.experiments || 'null') as string) as number[]
}

export const getUserFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.user || 'null') as string) as FrontendUser
}

export const getUserDetailedFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.user || 'null') as string) as FrontendUserDetailed
}

export const getPostsFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.posts || 'null') as string) as CompactGhostPost[]
}

export const getPostFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.post || 'null') as string) as GhostPost
}

export const getPostConfigFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.postConfig || 'null') as string) as BlogPostConfig
}

export const getPreviewFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.preview || 'null') as string) as Preview
}

export const getInfluencerFromQuery = (query: ParsedUrlQuery) => {
  return JSON.parse((query.influencer || 'null') as string) as TInfluencer
}

export const getPreOnboardingTutorialStep = (query: ParsedUrlQuery) => {
  const user = getUserDetailedFromQuery(query)

  const isPreOnboardingTutorialDone =
    query.preOnboardingTutorialDone ||
    [
      PreOnboardingTutorialStep.beginner,
      PreOnboardingTutorialStep.aspiring,
      PreOnboardingTutorialStep.professional,
    ].includes(user?.prefs?.musicMakingLevel as any)

  return isPreOnboardingTutorialDone ? PreOnboardingTutorialStep.done : (query.preOnboardingTutorialStep as string)
}

export const isInStandaloneMode = () =>
  (typeof window !== undefined && window.matchMedia('(display-mode: standalone)').matches) ||
  // @ts-ignore
  window.navigator.standalone ||
  document.referrer.includes('android-app://')

export const getTranslateFromLocale = async (locale: string | undefined, pathnameRaw: string) => {
  const pathname = pathnameRaw.replaceAll('//', '/')

  const indexPaths = ['/pages/studio', '/pages/track-separation', '/pages/mastering', '/pages/learn-chords']

  const language = locale || 'en'
  const fileName = pathname === '/pages/' || indexPaths.some((p) => pathname.startsWith(p)) ? '/pages/index' : pathname

  try {
    const file = await import(`../translations/${language}${fileName}.json`).catch(() => {})
    if (file) return file.default

    const fileInFolder = await import(`../translations/${language}${fileName}/index.json`).catch(() => {})
    if (fileInFolder) return fileInFolder.default

    const globalFile = await import(`../translations/${language}/pages/global.json`).catch(() => {})
    if (globalFile) return globalFile.default

    return {}
  } catch (e) {
    return {}
  }
}

export const getPageText = async (locale: string | undefined, pathname: string) => {
  return await getTranslateFromLocale(locale, `/pages${pathname}`)
}

export const getComponentText = async (locale: string | undefined, componentName: string) => {
  return await getTranslateFromLocale(locale, `/components/${componentName}`)
}

export const getClearPageUrl = (locale: string, asPath?: string, pathname?: string) => {
  const url = ((asPath?.split('?')[0] || pathname) + '/').replace(`/${locale}/`, '/')
  if (url === '/') return url
  return url.slice(0, -1)
}
